import React, { useState } from 'react'
import {
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material'
import { useProfiles } from '../../context/ProfileContext'
import { generateResumeWithAI } from '../../firebase/functionsService'

const ResumeEditor = () => {
  const { profiles, loadProfile } = useProfiles()
  const [selectedProfileId, setSelectedProfileId] = useState('')
  const [selectedProfile, setSelectedProfile] = useState(null)
  const [jobDescription, setJobDescription] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedResume, setGeneratedResume] = useState('')
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' })

  const handleProfileChange = async (event) => {
    const profileId = event.target.value
    setSelectedProfileId(profileId)

    if (profileId) {
      try {
        const result = await loadProfile(profileId)
        if (result.success) {
          setSelectedProfile(result.data)
        }
      } catch (error) {
        console.error('Error loading profile:', error)
      }
    } else {
      setSelectedProfile(null)
    }
  }

  const handleJobDescriptionChange = (event) => {
    setJobDescription(event.target.value)
  }

  const handleCreateResume = async () => {
    // Validate inputs
    if (!selectedProfileId) {
      setNotification({
        open: true,
        message: 'Please select a profile first',
        severity: 'error',
      })
      return
    }

    if (!jobDescription.trim()) {
      setNotification({
        open: true,
        message: 'Please enter a job description',
        severity: 'error',
      })
      return
    }

    setIsGenerating(true)
    setGeneratedResume('') // Clear any previous resume

    try {
      // Call the Firebase function to generate the resume
      const result = await generateResumeWithAI(selectedProfile, jobDescription)

      if (result.success) {
        setGeneratedResume(result.generatedResume)
        setNotification({
          open: true,
          message: 'Resume generated successfully!',
          severity: 'success',
        })
      } else {
        throw new Error(result.error || 'Failed to generate resume')
      }
    } catch (error) {
      setNotification({
        open: true,
        message: `Error generating resume: ${error.message}`,
        severity: 'error',
      })
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false })
  }

  return (
    <>
      <Paper
        sx={{
          p: 3,
          width: "clamp('200px', '40%', '400px')",
          margin: 1,
        }}
        elevation={2}
      >
        <Typography variant='h5' gutterBottom>
          Resume Generator
        </Typography>

        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id='profile-select-label'>Select Profile</InputLabel>
            <Select
              labelId='profile-select-label'
              id='profile-select'
              value={selectedProfileId}
              label='Select Profile'
              onChange={handleProfileChange}
              disabled={isGenerating}
            >
              <MenuItem value=''>
                <em>None</em>
              </MenuItem>
              {profiles &&
                profiles.map((profile) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.id.replace(/_/g, ' ')}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            label='Job Description'
            multiline
            rows={8}
            value={jobDescription}
            onChange={handleJobDescriptionChange}
            fullWidth
            placeholder='Paste the job description here...'
            disabled={isGenerating}
            sx={{ mb: 3 }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='contained'
            color='primary'
            onClick={handleCreateResume}
            disabled={isGenerating}
            startIcon={isGenerating ? <CircularProgress size={20} /> : null}
            size='large'
          >
            {isGenerating ? 'Creating...' : 'Create Resume'}
          </Button>
        </Box>
      </Paper>

      {/* Display generated resume */}
      {generatedResume && (
        <Paper
          sx={{
            p: 3,
            margin: 1,
            marginTop: 3,
            backgroundColor: '#f9f9f9',
            maxHeight: '600px',
            overflow: 'auto',
          }}
          elevation={3}
        >
          <Typography variant='h6' gutterBottom>
            Generated Resume
          </Typography>
          <Box sx={{ whiteSpace: 'pre-line' }}>
            <Typography variant='body1'>{generatedResume}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant='outlined'
              onClick={() => {
                navigator.clipboard.writeText(generatedResume)
                setNotification({
                  open: true,
                  message: 'Resume copied to clipboard!',
                  severity: 'success',
                })
              }}
            >
              Copy to Clipboard
            </Button>
          </Box>
        </Paper>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default ResumeEditor
